import type { StateCreator } from "zustand"
import { useMemo } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import type {
	CompaniesFilterNegations,
	SourceValue,
} from "@productlane/zero/queries"

import type { NumericFilter } from "@/components/filters"
import { withoutNegation } from "@/components/filters"

export type CompaniesFilterNegationKey = keyof CompaniesFilterNegations

interface CompaniesFilterState {
	negations: CompaniesFilterNegations
	setNegation: (key: CompaniesFilterNegationKey, isNegated: boolean) => void

	tiers: string[]
	addTier: (tier: string) => void
	removeTier: (tier: string) => void
	clearTiers: () => void

	statuses: string[]
	addStatus: (status: string) => void
	removeStatus: (status: string) => void
	clearStatuses: () => void

	ownerIds: Array<string | null>
	addOwner: (ownerId: string | null) => void
	removeOwner: (ownerId: string | null) => void
	clearOwners: () => void

	revenue: NumericFilter | null
	setRevenue: (filter: NumericFilter | null) => void

	size: NumericFilter | null
	setSize: (filter: NumericFilter | null) => void

	sources: SourceValue[]
	addSource: (value: SourceValue) => void
	removeSource: (value: SourceValue) => void
	clearSources: () => void

	reset: () => void
}

const filterDefinition: StateCreator<CompaniesFilterState> = (set) => ({
	negations: {},
	setNegation: (key, isNegated) => {
		set((prev) => ({
			negations: isNegated
				? { ...prev.negations, [key]: true }
				: withoutNegation(prev.negations, key),
		}))
	},

	tiers: [],
	addTier: (tier) => {
		set((prev) => ({ tiers: [...prev.tiers, tier] }))
	},
	removeTier: (tier) => {
		set((prev) => ({ tiers: prev.tiers.filter((t) => t !== tier) }))
	},
	clearTiers: () => {
		set((prev) => ({
			tiers: [],
			negations: withoutNegation(prev.negations, "tiers"),
		}))
	},

	statuses: [],
	addStatus: (status) => {
		set((prev) => ({ statuses: [...prev.statuses, status] }))
	},
	removeStatus: (status) => {
		set((prev) => ({ statuses: prev.statuses.filter((s) => s !== status) }))
	},
	clearStatuses: () => {
		set((prev) => ({
			statuses: [],
			negations: withoutNegation(prev.negations, "statuses"),
		}))
	},

	ownerIds: [],
	addOwner: (ownerId) => {
		set((prev) => ({ ownerIds: [...prev.ownerIds, ownerId] }))
	},
	removeOwner: (ownerId) => {
		set((prev) => ({ ownerIds: prev.ownerIds.filter((id) => id !== ownerId) }))
	},
	clearOwners: () => {
		set((prev) => ({
			ownerIds: [],
			negations: withoutNegation(prev.negations, "ownerIds"),
		}))
	},

	revenue: null,
	setRevenue: (filter) => {
		set({ revenue: filter })
	},

	size: null,
	setSize: (filter) => {
		set({ size: filter })
	},

	sources: [],
	addSource: (value) => {
		set((prev) => ({ sources: [...prev.sources, value] }))
	},
	removeSource: (value) => {
		set((prev) => ({ sources: prev.sources.filter((v) => v !== value) }))
	},
	clearSources: () => {
		set((prev) => ({
			sources: [],
			negations: withoutNegation(prev.negations, "sources"),
		}))
	},

	reset: () => {
		set({
			negations: {},
			tiers: [],
			statuses: [],
			ownerIds: [],
			revenue: null,
			size: null,
			sources: [],
		})
	},
})

export const useCompaniesFilterState = create<CompaniesFilterState>()(
	persist(filterDefinition, { name: "companies-filter-state" }),
)

export function useIsCompaniesFilterActive() {
	const { tiers, statuses, ownerIds, revenue, size, sources } =
		useCompaniesFilterState()
	return useMemo(
		() =>
			tiers.length > 0 ||
			statuses.length > 0 ||
			ownerIds.length > 0 ||
			revenue !== null ||
			size !== null ||
			sources.length > 0,
		[
			tiers.length,
			statuses.length,
			ownerIds.length,
			revenue,
			size,
			sources.length,
		],
	)
}
